import { useMemo } from 'react'

import { useSelector } from 'react-redux'

import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { ScheduleSteps } from 'src/Types/ui'
import { submitStripePayment } from 'src/Redux/payment-process/api-actions'
import { setPaymentAlertError } from 'src/Redux/payment-process'
import { PaymentErrors } from 'src/Components/PaymentErrorsAlert'
import { getScheduledTikTokCampaign } from 'src/Redux/schedule-process/tiktokCampaign/selectors'
import { getTiktokPrice } from 'src/Redux/schedule-process/audienceTiktokSlice/selectors'
import { getValidatedStartDate } from 'src/Constants/functions'
import { spotifyTotalPriceSelector } from 'src/Redux/spotifyCampaignMatchingProcess'
import { useBudgetRange } from 'src/Redux/spotifyBudgetRangeApi'
import { useSpotifyCampaignStatus } from 'src/Redux/spotifyCampaignStatusApi'
import { selectManualMatchingBudget } from 'src/Redux/selectedCuratorsSlice'

import { ArtistRoutesEnum } from '../Router/ArtistComponentsNewUI'
import { PaymentStatusEnum } from '../Pages/Artist/SpotifySchedulePage/helpers/useBackFromPaymentPageSpotify'

import { useAppDispatch, useAppSelector } from './redux'
import { useHasPlan } from './useHasPlan'
import { getIsManualMatchingCampaign } from './Experiments/useManualMatchingExperiment'

export type ClickContinueParams = {
  paymentAmount: number
  usedBalance: number
  coupon: string
}

interface UseStripePayment {
  pay: (params: ClickContinueParams) => void
}

interface CreatePaymentUrlParams {
  campaignId: number | string
  startDate: string
  type: CampaignPlatformTypeEnum
  budget?: number | string
  coupon?: string
  isLowReachCampaign?: boolean
}

const getBaseUrlForPayment = (type: CampaignPlatformTypeEnum): string => {
  const routeMap: Record<CampaignPlatformTypeEnum, string> = {
    [CampaignPlatformTypeEnum.SPOTIFY]: ArtistRoutesEnum.SpotifySchedule,
    [CampaignPlatformTypeEnum.TIKTOK]: ArtistRoutesEnum.TiktokSchedule,
    [CampaignPlatformTypeEnum.META]: ArtistRoutesEnum.AdsSchedule,
    [CampaignPlatformTypeEnum.REELS]: '',
    [CampaignPlatformTypeEnum.SPOTIFY_TIKTOK]: '',
  }

  const route = routeMap[type] ?? ArtistRoutesEnum.SpotifySchedule
  return `${window.location.origin}${route}`
}

export const createSuccessUrl = ({
  campaignId,
  startDate,
  type,
  isLowReachCampaign = false,
}: CreatePaymentUrlParams): string => {
  const baseUrl = getBaseUrlForPayment(type)

  const isLowReachCampaignString = isLowReachCampaign
    ? 'isLowReachCampaign=true'
    : ''

  return `${baseUrl}/${campaignId}?paymentStatus=${PaymentStatusEnum.SUCCESS}&type=${type}&startDate=${startDate}&step=${ScheduleSteps.SETTINGS}&${isLowReachCampaignString}`
}

export const createCancelUrl = ({
  campaignId,
  startDate,
  type,
  budget = '',
  coupon = '',
}: CreatePaymentUrlParams): string => {
  const baseUrl = getBaseUrlForPayment(type)

  return `${baseUrl}/${campaignId}?paymentStatus=${PaymentStatusEnum.FAILURE}&budget=${budget}&coupon=${coupon}&type=${type}&startDate=${startDate}&step=${ScheduleSteps.SETTINGS}`
}

export const createPaymentUrls = (
  params: CreatePaymentUrlParams,
): {
  successUrl: string
  cancelUrl: string
} => ({
  successUrl: createSuccessUrl(params),
  cancelUrl: createCancelUrl(params),
})

const FIXED_BY = 2

export const useStripePayment = (
  platformType: CampaignPlatformTypeEnum,
): UseStripePayment => {
  const dispatch = useAppDispatch()
  const { isLowReachError } = useBudgetRange()

  const { campaignDetails: spotifyCampaignDetails, matchingType } =
    useSpotifyCampaignStatus()
  const spotifyTotalPrice = useAppSelector(spotifyTotalPriceSelector)
  const manualMatchingBudget = useAppSelector(selectManualMatchingBudget)
  const scheduledCampaign = useSelector(getScheduledTikTokCampaign)
  const price = useAppSelector(getTiktokPrice)
  const hasPlan = useHasPlan()

  const isManualMatching = getIsManualMatchingCampaign(matchingType)
  const spotifyPrice = isManualMatching
    ? manualMatchingBudget
    : spotifyTotalPrice

  const campaignToPay = useMemo(() => {
    let campaign

    if (platformType === CampaignPlatformTypeEnum.SPOTIFY) {
      campaign = {
        price: spotifyPrice,
        genres: spotifyCampaignDetails?.genres || [],
        languages: spotifyCampaignDetails?.languages || [],
        id: spotifyCampaignDetails?.id || 0,
        startDate: spotifyCampaignDetails?.start_date || '',
      }
    } else {
      campaign = {
        price,
        id: scheduledCampaign.id,
        startDate: scheduledCampaign.startDate,
      }
    }

    return campaign
  }, [
    platformType,
    price,
    scheduledCampaign.id,
    scheduledCampaign.startDate,
    spotifyCampaignDetails?.genres,
    spotifyCampaignDetails?.id,
    spotifyCampaignDetails?.languages,
    spotifyCampaignDetails?.start_date,
    spotifyPrice,
  ])

  const pay = (params: ClickContinueParams): void => {
    if (!campaignToPay.price) {
      return
    }

    const paymentUrls = createPaymentUrls({
      campaignId: Number(campaignToPay.id),
      startDate: getValidatedStartDate(campaignToPay.startDate, hasPlan),
      type: platformType,
      budget: Number(campaignToPay.price.toFixed(FIXED_BY)),
      coupon: params.coupon,
      isLowReachCampaign: isLowReachError,
    })

    params.paymentAmount &&
      void dispatch(
        submitStripePayment({
          amount: params.paymentAmount,
          campaignValue: Number(campaignToPay.price.toFixed(FIXED_BY)),
          campaignId: Number(campaignToPay.id),
          startDate: getValidatedStartDate(campaignToPay.startDate, hasPlan),
          couponValue: params.coupon,
          type: platformType,
          usedBalance: params.usedBalance,
          successUrl: paymentUrls.successUrl,
          cancelUrl: paymentUrls.cancelUrl,
        }),
      )
        .unwrap()
        .then((res) => {
          window.location.href = res.link
          return res
        })
        .catch(() => {
          dispatch(setPaymentAlertError(PaymentErrors.DEFAULT))
        })
  }

  return {
    pay,
  }
}
